import { useEffect, useState } from 'react';
import {
  Stack, Typography, Box, Chip, Tooltip, Link, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { AxisCard, NotCollected, AxisLoading, AxisError } from './ProductAxisCard';

// 사이버보안 점검 대상 — 이 제품을 이루는 코드베이스가 무엇이고, 실제로 점검이 걸려 있는가.
// 데이터: GET /api/security/scope (cloud-compliance 의 점검 대장 + Dependency-Track 실측)
//
// 🔴 옆 카드(보안)와 **다른 질문**에 답한다. 섞으면 조치 대상이 흐려진다.
//   보안 카드 — GitHub Dependabot 이 본 취약점이 몇 건인가
//   이 카드  — 그 취약점을 세는 **대상 자체**가 맞게 잡혀 있는가 (Dependency-Track 기준)
//   숫자가 다른 것이 정상이다. 보는 도구와 보는 범위가 다르다.
//
// 🔴 상태 라벨을 화면이 만들지 않는다. 피드가 legend 를 싣고 이 카드는 그대로 쓴다.
//   판정 기준이 바뀔 때 화면이 따라오지 못하면, 그때 사람들은 화면을 믿는다.
//
// 🔴 "점검 대상 밖"(gap)을 비워 두지 않는다. 취약점이 없는 것이 아니라 **아무도 보지 않고 있다**.

const STATE_COLOR = (theme, s) => {
  switch (s) {
    case 'watched': return theme.palette.success.main;
    case 'stale': return theme.palette.warning.main;
    case 'gap':
    case 'never': return theme.palette.error.main;
    default: return theme.palette.text.disabled;
  }
};

export default function ProductScopeCard({ productId }) {
  const [state, setState] = useState({ data: null, error: null, loading: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/security/scope');
        const body = await res.json();
        if (!alive) return;
        if (!res.ok) {
          setState({ data: null, error: body?.hint || body?.error || `HTTP ${res.status}`, loading: false });
          return;
        }
        setState({ data: body, error: null, loading: false });
      } catch (e) {
        if (alive) setState({ data: null, error: String(e), loading: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  const theme = useTheme();
  const { data, error, loading } = state;

  // 점검 대장의 제품 ↔ 콘솔 제품은 prd-* 저장소 이름으로 잇는다.
  const row = data?.products?.find((p) => p.harness?.prd_repo === productId) || null;
  const legend = data?.legend || {};

  const card = (children) => (
    <AxisCard
      title="점검 대상"
      icon={<InventoryIcon sx={{ fontSize: 19, color: 'text.secondary' }} />}
      source="cloud-compliance · security-monitoring-scope.yaml"
      sourceUrl="https://github.com/rayrndsw/cloud-compliance/blob/main/operations/registers/security-monitoring-scope.yaml"
      updatedAt={data?.as_of || null}
    >
      {children}
    </AxisCard>
  );

  if (loading) return card(<AxisLoading />);
  if (error) return card(<AxisError message="점검 대상 대장을 불러오지 못했습니다" hint={error} />);
  if (!row) {
    return card(
      <NotCollected
        reason={
          '이 제품이 사이버보안 점검 대장에 없습니다. 담당자가 신청서로 등록해야 '
          + '점검 대상에 들어갑니다 — 등록되지 않은 SW 는 취약점이 없는 것이 아니라 아무도 보지 않는 상태입니다.'
        }
      />,
    );
  }

  const repos = row.repos || [];
  const counts = row.counts || {};
  const sum = (k) => repos.reduce((a, r) => a + (r[k] || 0), 0);

  return card(
    <Stack spacing={1.5}>
      {/* 제품이 어떤 것인지 — 담당자가 자기 제품을 알아볼 수 있게 */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={row.regulatory_label || '허가 여부 미상'} variant="outlined" />
        {row.regulatory_no && <Chip size="small" label={row.regulatory_no} variant="outlined" />}
        {row.delivery && <Chip size="small" label={row.delivery} variant="outlined" />}
        {row.build && <Chip size="small" label={row.build} variant="outlined" />}
        {row.harness?.uses_harness === true && (
          <Tooltip title={legend.harness_why?.true || ''}>
            <Chip size="small" color="primary" label="🔧 rederi harness" />
          </Tooltip>
        )}
        {row.harness?.uses_harness === null && (
          <Tooltip title={legend.harness_why?.null || ''}>
            <Chip size="small" label="❓ harness 확인 못 함" variant="outlined" />
          </Tooltip>
        )}
      </Stack>

      {/* 상태 요약 — '몇 %' 가 아니라 '무엇이 몇 개' */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {['watched', 'stale', 'never', 'gap', 'unknown'].map((s) => (
          counts[s] ? (
            <Tooltip key={s} title={legend.state_why?.[s] || ''}>
              <Chip
                size="small"
                label={`${legend.state?.[s] || s} ${counts[s]}`}
                sx={{ bgcolor: 'transparent', border: `1px solid ${STATE_COLOR(theme, s)}`, color: STATE_COLOR(theme, s) }}
              />
            </Tooltip>
          ) : null
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Critical {sum('critical')} · 미판정 {sum('unaudited')}
        </Typography>
      </Stack>

      {/* 🔴 코드베이스를 공유하는 제품은 저장소가 비어 있다 — 왜 비었는지 말해 준다 */}
      {row.shares_codebase_with && (
        <Typography variant="caption" color="text.secondary">
          이 제품은 <b>{row.shares_codebase_with}</b> 와 같은 코드베이스에서 나옵니다.
          점검은 저장소 단위로 한 번만 돌립니다 — 같은 코드를 두 번 세지 않기 위해서입니다.
        </Typography>
      )}
      {row.also_builds?.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          이 저장소에서 함께 나오는 제품: {row.also_builds.join(' · ')}
        </Typography>
      )}

      {repos.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>코드베이스</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="right">마지막 수집</TableCell>
              <TableCell align="right">Critical</TableCell>
              <TableCell align="right">미판정</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repos.map((r) => (
              <TableRow key={r.repo}>
                <TableCell>
                  <Link href={r.url} target="_blank" rel="noreferrer" underline="hover">{r.repo}</Link>
                  {r.note && (
                    <Typography variant="caption" color="text.secondary" display="block">{r.note}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={legend.state_why?.[r.state] || ''}>
                    <Typography variant="caption" sx={{ color: STATE_COLOR(theme, r.state) }}>
                      {legend.state?.[r.state] || r.state}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {r.last_seen_days == null ? '—' : `${r.last_seen_days}일 전`}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.critical ?? '—'}</TableCell>
                <TableCell align="right">{r.unaudited ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" color="text.secondary">
        구성이 틀리거나 빠진 저장소가 있으면 {data?.review?.register_owner || '담당자'}에게 알려주세요 — 대장을 고칩니다.
        {' '}미판정은 "안전"이 아니라 "아직 아무도 보지 않았다" 는 뜻입니다.
      </Typography>
    </Stack>,
  );
}
